import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { adminMessagingApi, auditorMessagingApi, companyMessagingApi, hospitalMessagingApi } from "../api/messagingApi";
import {
  getAccountTypeRoute,
  getAuthToken,
  getStoredAccountType,
  type AccountType,
} from "../auth";
import {
  adminChatMessages,
  adminMessageThreads,
  auditorChatMessages,
  auditorMessageThreads,
  enterpriseChatMessages,
  enterpriseMessageThreads,
  hospitalChatMessages,
  hospitalMessageThreads,
} from "../data/messaging";
import type { ChatMessage, MessageThread } from "../types";
import { formatMessageNow } from "./utils";

export type FloatingChatState = "hidden" | "collapsed" | "expanded";

type MessagingContextValue = {
  isAvailable: boolean;
  homeHref: string;
  subtitle: string;
  threads: MessageThread[];
  messages: ChatMessage[];
  selectedThreadId: string | null;
  selectedThread: MessageThread | null;
  threadMessages: ChatMessage[];
  draft: string;
  loading: boolean;
  error: string | null;
  usingApi: boolean;
  floatingState: FloatingChatState;
  setDraft: (value: string) => void;
  selectThread: (threadId: string) => void;
  sendMessage: () => void;
  minimizeToFloating: () => void;
  collapseFloating: () => void;
  expandFloating: () => void;
  closeFloating: () => void;
  openFloating: () => void;
  updateThreads: React.Dispatch<React.SetStateAction<MessageThread[]>>;
};

const MessagingContext = createContext<MessagingContextValue | null>(null);

function getMessagingConfig(accountType: AccountType | null) {
  if (accountType === "hospital") {
    return {
      isAvailable: true,
      homeHref: getAccountTypeRoute("hospital"),
      subtitleKey: "hospital" as const,
      threads: hospitalMessageThreads,
      messages: hospitalChatMessages,
      api: hospitalMessagingApi,
      fallbackThreads: hospitalMessageThreads,
      fallbackMessages: hospitalChatMessages,
    };
  }

  if (accountType === "admin") {
    return {
      isAvailable: true,
      homeHref: getAccountTypeRoute("admin"),
      subtitleKey: "admin" as const,
      threads: adminMessageThreads,
      messages: adminChatMessages,
      api: adminMessagingApi,
      fallbackThreads: adminMessageThreads,
      fallbackMessages: adminChatMessages,
    };
  }

  if (accountType === "auditor") {
    return {
      isAvailable: true,
      homeHref: getAccountTypeRoute("auditor"),
      subtitleKey: "auditor" as const,
      threads: auditorMessageThreads,
      messages: auditorChatMessages,
      api: auditorMessagingApi,
      fallbackThreads: auditorMessageThreads,
      fallbackMessages: auditorChatMessages,
    };
  }

  if (accountType === "enterprise") {
    return {
      isAvailable: true,
      homeHref: getAccountTypeRoute("enterprise"),
      subtitleKey: "enterprise" as const,
      threads: enterpriseMessageThreads,
      messages: enterpriseChatMessages,
      api: companyMessagingApi,
      fallbackThreads: enterpriseMessageThreads,
      fallbackMessages: enterpriseChatMessages,
    };
  }

  return {
    isAvailable: false,
    homeHref: "/dashboard",
    subtitleKey: "enterprise" as const,
    threads: [] as MessageThread[],
    messages: [] as ChatMessage[],
    api: null as typeof companyMessagingApi | null,
    fallbackThreads: [] as MessageThread[],
    fallbackMessages: [] as ChatMessage[],
  };
}

type MessagingProviderProps = {
  children: ReactNode;
};

export function MessagingProvider({ children }: MessagingProviderProps) {
  const { t } = useTranslation();
  const subtitles = t.safecheck.messaging.subtitles;
  const [accountType] = useState(() => getStoredAccountType());
  const config = useMemo(() => getMessagingConfig(accountType), [accountType]);
  const useApi = Boolean(config.api) && Boolean(getAuthToken());

  const [threads, setThreads] = useState<MessageThread[]>(config.threads);
  const [messages, setMessages] = useState<ChatMessage[]>(config.messages);
  const [selectedThreadId, setSelectedThreadId] = useState<string | null>(
    config.threads[0]?.id ?? null,
  );
  const [draft, setDraft] = useState("");
  const [floatingState, setFloatingState] = useState<FloatingChatState>("hidden");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [usingApi, setUsingApi] = useState(useApi);

  const subtitle = subtitles[config.subtitleKey];
  const api = config.api;

  const selectedThread =
    threads.find((thread) => thread.id === selectedThreadId) ?? null;

  const threadMessages = useMemo(
    () =>
      messages
        .filter((message) => message.threadId === selectedThreadId)
        .sort((a, b) => {
          const dateA = `${a.date} ${a.time}`;
          const dateB = `${b.date} ${b.time}`;
          return dateA.localeCompare(dateB);
        }),
    [messages, selectedThreadId],
  );

  useEffect(() => {
    if (!useApi || !api) {
      setUsingApi(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setError(null);
      setUsingApi(true);

      try {
        const apiThreads = await api.fetchThreads();
        if (cancelled) return;

        setThreads(apiThreads);
        const firstId = apiThreads[0]?.id ?? null;
        setSelectedThreadId(firstId);

        if (firstId) {
          const apiMessages = await api.fetchMessages(firstId);
          if (cancelled) return;
          setMessages(apiMessages);
          await api.markRead(firstId);
          if (cancelled) return;
          setThreads((current) =>
            current.map((thread) =>
              thread.id === firstId ? { ...thread, unreadCount: 0 } : thread,
            ),
          );
        } else {
          setMessages([]);
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Impossible de charger la messagerie.");
        setThreads(config.fallbackThreads);
        setMessages(config.fallbackMessages);
        setSelectedThreadId(config.fallbackThreads[0]?.id ?? null);
        setUsingApi(false);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void load();

    return () => {
      cancelled = true;
    };
  }, [useApi, api, config.fallbackMessages, config.fallbackThreads]);

  const selectThread = useCallback(
    (threadId: string) => {
      setSelectedThreadId(threadId);
      setThreads((current) =>
        current.map((thread) =>
          thread.id === threadId ? { ...thread, unreadCount: 0 } : thread,
        ),
      );

      if (!useApi || !usingApi || !api) {
        return;
      }

      void (async () => {
        try {
          const apiMessages = await api.fetchMessages(threadId);
          setMessages((current) => [
            ...current.filter((message) => message.threadId !== threadId),
            ...apiMessages,
          ]);
          await api.markRead(threadId);
        } catch (err) {
          setError(err instanceof Error ? err.message : "Impossible de charger la conversation.");
        }
      })();
    },
    [useApi, usingApi, api],
  );

  const sendMessage = useCallback(() => {
    const content = draft.trim();
    if (!content || !selectedThreadId) return;

    if (!useApi || !usingApi || !api) {
      const { date, time } = formatMessageNow();
      const newMessage: ChatMessage = {
        id: `msg-${Date.now()}`,
        threadId: selectedThreadId,
        sender: "me",
        content,
        time,
        date,
      };

      setMessages((current) => [...current, newMessage]);
      setThreads((current) =>
        current.map((thread) =>
          thread.id === selectedThreadId
            ? {
                ...thread,
                lastMessage: content,
                lastMessageAt: time,
                lastMessageDate: date,
              }
            : thread,
        ),
      );
      setDraft("");
      return;
    }

    void (async () => {
      try {
        setError(null);
        const created = await api.sendMessage(selectedThreadId, content);
        setMessages((current) => [...current, created]);
        setThreads((current) =>
          current.map((thread) =>
            thread.id === selectedThreadId
              ? {
                  ...thread,
                  lastMessage: created.content,
                  lastMessageAt: created.time,
                  lastMessageDate: created.date,
                  unreadCount: 0,
                }
              : thread,
          ),
        );
        setDraft("");
      } catch (err) {
        setError(err instanceof Error ? err.message : "Envoi impossible.");
      }
    })();
  }, [draft, selectedThreadId, useApi, usingApi, api]);

  const minimizeToFloating = useCallback(() => {
    setFloatingState("expanded");
  }, []);

  const collapseFloating = useCallback(() => {
    setFloatingState("collapsed");
  }, []);

  const expandFloating = useCallback(() => {
    setFloatingState("expanded");
  }, []);

  const closeFloating = useCallback(() => {
    setFloatingState("hidden");
  }, []);

  const openFloating = useCallback(() => {
    setFloatingState("expanded");
  }, []);

  const value = useMemo<MessagingContextValue>(
    () => ({
      isAvailable: config.isAvailable,
      homeHref: config.homeHref,
      subtitle,
      threads,
      messages,
      selectedThreadId,
      selectedThread,
      threadMessages,
      draft,
      loading,
      error,
      usingApi,
      floatingState,
      setDraft,
      selectThread,
      sendMessage,
      minimizeToFloating,
      collapseFloating,
      expandFloating,
      closeFloating,
      openFloating,
      updateThreads: setThreads,
    }),
    [
      config.isAvailable,
      config.homeHref,
      subtitle,
      threads,
      messages,
      selectedThreadId,
      selectedThread,
      threadMessages,
      draft,
      loading,
      error,
      usingApi,
      floatingState,
      selectThread,
      sendMessage,
      minimizeToFloating,
      collapseFloating,
      expandFloating,
      closeFloating,
      openFloating,
    ],
  );

  if (!config.isAvailable) {
    return <>{children}</>;
  }

  return (
    <MessagingContext.Provider value={value}>{children}</MessagingContext.Provider>
  );
}

export function useMessaging() {
  const context = useContext(MessagingContext);
  if (!context) {
    throw new Error("useMessaging must be used within MessagingProvider");
  }
  return context;
}

export function useMessagingOptional() {
  return useContext(MessagingContext);
}
